import { useCallback } from 'react'
import type {
  UseStreamConnectionReturn,
  HostStatusChangedPayload,
  SessionStatusChangedPayload,
} from '@/infra'
import {
  useStreamConnection,
  isHostStatusChangedPayload,
  isSessionStatusChangedPayload,
} from '@/infra'
import { STATUS_STREAM_CONFIG } from '@/infra/sse/shared/config'

export interface UseStatusEventsStreamOptions {
  readonly accessToken: string | undefined
  readonly enabled: boolean
  readonly onSessionStatusChange?: ((event: SessionStatusChangedPayload) => void) | undefined
  readonly onHostStatusChange?: ((event: HostStatusChangedPayload) => void) | undefined
  readonly onConnected?: ((connectionId: string) => void) | undefined
  readonly onDisconnected?: (() => void) | undefined
  readonly onError?: ((error: Error) => void) | undefined
}

export function useStatusEventsStream(
  options: UseStatusEventsStreamOptions,
): UseStreamConnectionReturn {
  const {
    accessToken,
    enabled,
    onSessionStatusChange,
    onHostStatusChange,
    onConnected,
    onDisconnected,
    onError,
  } = options

  const handleEvent = useCallback(
    (data: string) => {
      try {
        const parsed: unknown = JSON.parse(data)
        if (isSessionStatusChangedPayload(parsed)) {
          onSessionStatusChange?.(parsed)
        } else if (isHostStatusChangedPayload(parsed)) {
          onHostStatusChange?.(parsed)
        } else {
          // eslint-disable-next-line no-console -- Intentional: unrecognized status event diagnostics
          console.warn('[SSE:status] Event matched neither session nor host guard:', parsed)
        }
      } catch {
        // Silently ignore malformed events
      }
    },
    [onSessionStatusChange, onHostStatusChange],
  )

  const handleError = useCallback(
    (error: { message: string }) => {
      onError?.(new Error(error.message))
    },
    [onError],
  )

  return useStreamConnection({
    config: STATUS_STREAM_CONFIG,
    accessToken,
    enabled,
    onEvent: handleEvent,
    onConnected,
    onDisconnected,
    onError: handleError,
  })
}
